import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [listingId, setListingId] = useState(null);
  const attempts = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!session_id) { setStatus("error"); return; }
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${session_id}`);
        setListingId(data.listing_id);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.payment_status === "expired" || data.status === "expired" || data.payment_status === "failed") {
          setStatus("failed");
          return;
        }
        attempts.current += 1;
        if (attempts.current > 30) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        setStatus("error");
      }
    };
    poll();
  }, [session_id]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-card border border-border rounded-md p-8 text-center">
        {status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto" />
            <h1 className="font-display font-bold text-2xl mt-4">Confirming payment…</h1>
            <p className="text-muted-foreground mt-2 text-sm">Hang tight, this only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-primary mx-auto" />
            <h1 className="font-display font-extrabold text-3xl mt-4">Payment complete</h1>
            <p className="text-muted-foreground mt-2">Your purchase is confirmed. The seller has been notified.</p>
            <div className="flex flex-col gap-2 mt-8">
              {listingId && <Button onClick={() => navigate(`/listings/${listingId}`)} variant="outline" data-testid="btn-view-listing">View listing</Button>}
              <Button onClick={() => navigate("/messages")} className="bg-primary hover:bg-primary/90" data-testid="btn-message-seller">Message the seller</Button>
              <Button onClick={() => navigate("/marketplace")} variant="ghost" data-testid="btn-continue-shopping">Back to marketplace</Button>
            </div>
          </>
        )}
        {(status === "failed" || status === "error" || status === "timeout") && (
          <>
            <XCircle className="w-14 h-14 text-destructive mx-auto" />
            <h1 className="font-display font-bold text-2xl mt-4">
              {status === "timeout" ? "Still processing" : "Payment issue"}
            </h1>
            <p className="text-muted-foreground mt-2 text-sm">
              {status === "timeout" ? "It's taking longer than expected. Check back shortly." : "We couldn't confirm your payment."}
            </p>
            <Button onClick={() => navigate("/marketplace")} className="mt-6 bg-primary hover:bg-primary/90" data-testid="btn-back-marketplace">Back to marketplace</Button>
          </>
        )}
      </div>
    </div>
  );
}
